/**
 * AUDIO — one-shot effects: footsteps, clue pickup (paper rustle + sting),
 * rotary phone dial for accusations, and win/lose resolution stingers.
 * Owned by AUDIO; see CONTRACTS.md.
 */
import type { ZoneId } from '../core/types'
import {
  CLUE_RUSTLE_BANDPASS_HZ,
  CLUE_RUSTLE_GAIN,
  CLUE_RUSTLE_Q,
  CLUE_RUSTLE_S,
  CLUE_STING_DECAY_S,
  CLUE_STING_FREQ_HZ,
  CLUE_STING_GAIN,
  DIAL_CLICK_GAIN,
  DIAL_CLICK_HIGHPASS_HZ,
  DIAL_CLICK_S,
  DIAL_DIGITS,
  DIAL_DIGIT_GAP_S,
  DIAL_PULSE_HZ,
  DIAL_START_DELAY_S,
  STEP_DECAY_S,
  STEP_GAIN,
  STEP_LOWPASS_INDOOR_HZ,
  STEP_LOWPASS_OUTDOOR_HZ,
  STEP_THUMP_GAIN,
  STEP_THUMP_HZ,
  STEP_VARIATION,
  STINGER_GAIN,
  STINGER_INTERVAL_S,
  STINGER_LOSE_FREQS_HZ,
  STINGER_NOTE_S,
  STINGER_WIN_FREQS_HZ
} from './constants'
import type { AudioEngine } from './engine'

const MIN_GAIN = 0.0001

/** Short filtered-noise burst — the building block for percussive SFX. */
const noiseBurst = (
  engine: AudioEngine,
  time: number,
  durationS: number,
  peakGain: number,
  filterType: BiquadFilterType,
  filterHz: number,
  filterQ = 1
): void => {
  const source = engine.ctx.createBufferSource()
  source.buffer = engine.noiseBuffer

  const filter = engine.ctx.createBiquadFilter()
  filter.type = filterType
  filter.frequency.value = filterHz
  filter.Q.value = filterQ

  const gain = engine.ctx.createGain()
  gain.gain.setValueAtTime(peakGain, time)
  gain.gain.exponentialRampToValueAtTime(MIN_GAIN, time + durationS)

  source.connect(filter)
  filter.connect(gain)
  gain.connect(engine.master)
  source.start(time)
  source.stop(time + durationS)
}

/** Decaying sine tone — stings and stingers. */
const toneBurst = (
  engine: AudioEngine,
  time: number,
  freq: number,
  durationS: number,
  peakGain: number,
  type: OscillatorType = 'sine'
): void => {
  const osc = engine.ctx.createOscillator()
  osc.type = type
  osc.frequency.value = freq

  const gain = engine.ctx.createGain()
  gain.gain.setValueAtTime(peakGain, time)
  gain.gain.exponentialRampToValueAtTime(MIN_GAIN, time + durationS)

  osc.connect(gain)
  gain.connect(engine.master)
  osc.start(time)
  osc.stop(time + durationS)
}

/** One footstep: filtered noise scuff + low thump. Surface varies by zone. */
export const playFootstep = (engine: AudioEngine, zone: ZoneId): void => {
  const now = engine.ctx.currentTime
  const baseCutoff = zone === 'indoor' ? STEP_LOWPASS_INDOOR_HZ : STEP_LOWPASS_OUTDOOR_HZ
  const variation = 1 + (Math.random() * 2 - 1) * STEP_VARIATION
  noiseBurst(engine, now, STEP_DECAY_S, STEP_GAIN, 'lowpass', baseCutoff * variation)
  toneBurst(engine, now, STEP_THUMP_HZ * variation, STEP_DECAY_S, STEP_THUMP_GAIN)
}

/** Paper rustle (two band-passed crinkles) plus a soft confirmation sting. */
export const playCluePickup = (engine: AudioEngine): void => {
  const now = engine.ctx.currentTime
  const secondCrinkleDelay = CLUE_RUSTLE_S * 0.5
  noiseBurst(engine, now, CLUE_RUSTLE_S, CLUE_RUSTLE_GAIN, 'bandpass', CLUE_RUSTLE_BANDPASS_HZ, CLUE_RUSTLE_Q)
  noiseBurst(
    engine,
    now + secondCrinkleDelay,
    CLUE_RUSTLE_S,
    CLUE_RUSTLE_GAIN * 0.7,
    'bandpass',
    CLUE_RUSTLE_BANDPASS_HZ * 1.3,
    CLUE_RUSTLE_Q
  )
  toneBurst(engine, now + secondCrinkleDelay, CLUE_STING_FREQ_HZ, CLUE_STING_DECAY_S, CLUE_STING_GAIN)
}

/** Rotary phone: pulse-train clicks per digit with a wind-back gap between. */
export const playPhoneDial = (engine: AudioEngine): void => {
  const pulseGap = 1 / DIAL_PULSE_HZ
  const start = engine.ctx.currentTime + DIAL_START_DELAY_S
  DIAL_DIGITS.reduce((digitStart, pulses) => {
    for (let i = 0; i < pulses; i += 1) {
      noiseBurst(
        engine,
        digitStart + i * pulseGap,
        DIAL_CLICK_S,
        DIAL_CLICK_GAIN,
        'highpass',
        DIAL_CLICK_HIGHPASS_HZ
      )
    }
    return digitStart + pulses * pulseGap + DIAL_DIGIT_GAP_S
  }, start)
}

/** Resolution stinger: rising minor arpeggio on win, falling tritone on loss. */
export const playResolution = (engine: AudioEngine, won: boolean): void => {
  const freqs = won ? STINGER_WIN_FREQS_HZ : STINGER_LOSE_FREQS_HZ
  const type: OscillatorType = won ? 'sine' : 'sawtooth'
  const start = engine.ctx.currentTime
  freqs.forEach((freq, i) => {
    toneBurst(engine, start + i * STINGER_INTERVAL_S, freq, STINGER_NOTE_S, STINGER_GAIN, type)
  })
}
